/**
 * Sprint O1 — Single production item card.
 *
 * One card = one OrderItem. Everything the operator needs at a glance:
 *   - Order context (invoice #, company, customer)
 *   - Item description + qty
 *   - Production status (editable inline dropdown)
 *   - Responsible employee (editable inline dropdown)
 *   - Countdown to delivery date
 *   - Red blocker chips that toggle on click (missing art / payment / etc.)
 */
import React, { useMemo } from "react";
import { useI18n } from "@/context/I18nContext";
import { DASHBOARD } from "@/constants/testIds";
import { formatDate } from "@/lib/dateUtils";
import CountdownBadge from "@/components/CountdownBadge";

const STATUSES = [
  "pending",
  "design",
  "ready_for_production",
  "in_production",
  "ready_for_delivery",
  "delivered",
];

// Color band by status — gives the eye a single glance read of the queue.
const STATUS_BAND = {
  pending: "bg-neutral-100 border-neutral-400 text-neutral-700",
  design: "bg-slate-100 border-slate-400 text-slate-800",
  ready_for_production: "bg-amber-100 border-amber-500 text-amber-900",
  in_production: "bg-blue-100 border-blue-500 text-blue-900",
  ready_for_delivery: "bg-emerald-100 border-emerald-500 text-emerald-900",
  delivered: "bg-neutral-200 border-neutral-500 text-neutral-600",
};

const initialsOf = (name) => {
  if (!name) return "??";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
};

// Maps the backend field name → the short i18n suffix used in the dictionary.
// (We use long backend names internally so the field reference is unambiguous,
// but the i18n keys are kept short for readability — production_blocker_art etc.)
const BLOCKER_OK_KEY = {
  art_approved: "art",
  payment_received: "payment",
  material_received: "material",
  client_approved: "approval",
};

export default function ProductionItemCard({
  item,
  urgency,
  employee,
  employees,
  method,
  onOpenOrder,
  onChangeStatus,
  onToggleBlocker,
  onAssign,
}) {
  const { t } = useI18n();

  const blockers = useMemo(() => {
    // Each tuple: [field, missingLabelKey, currentlyOK]
    return [
      ["art_approved", "production_blocker_missing_art", item.art_approved, DASHBOARD.productionBlockerArt(item.item_id)],
      ["payment_received", "production_blocker_missing_payment", item.payment_received, DASHBOARD.productionBlockerPayment(item.item_id)],
      ["material_received", "production_blocker_missing_material", item.material_received, DASHBOARD.productionBlockerMaterial(item.item_id)],
      ["client_approved", "production_blocker_missing_approval", item.client_approved, DASHBOARD.productionBlockerApproval(item.item_id)],
    ];
  }, [item]);

  const missingCount = blockers.filter(([, , ok]) => !ok).length;
  const band = STATUS_BAND[item.production_status] || STATUS_BAND.pending;

  return (
    <article
      data-testid={DASHBOARD.productionCard(item.order_id, item.item_id)}
      className={`border-l-4 ${band.split(" ").find((c) => c.startsWith("border-"))} bg-white border border-[var(--border)] flex flex-col`}
    >
      {/* Top band — invoice + countdown */}
      <header className="flex items-start justify-between p-3 gap-2 border-b border-[var(--border)]">
        <button
          type="button"
          data-testid={DASHBOARD.productionOpenOrderBtn(item.order_id)}
          onClick={onOpenOrder}
          className="text-left min-w-0 flex-1 group"
          title={t("production_open_order")}
        >
          <div className="font-display font-bold text-sm group-hover:underline truncate">
            #{item.invoice_number || item.order_id?.slice(0, 6)}
          </div>
          <div className="text-xs text-[var(--text-secondary)] truncate">
            {item.company_name || "—"}
            {item.customer_name && item.customer_name !== item.company_name
              ? ` · ${item.customer_name}`
              : ""}
          </div>
        </button>
        <div className="flex-shrink-0">
          <CountdownBadge date={item.delivery_date} urgency={urgency} />
        </div>
      </header>

      {/* Body — item info */}
      <div className="p-3 space-y-3 flex-1">
        <div>
          <div className="font-display font-bold text-base leading-tight">
            {item.description || item.product_code || "—"}
          </div>
          <div className="flex items-center gap-2 mt-1 font-mono-tech text-[0.7rem] tracking-wider text-[var(--text-secondary)]">
            <span>QTY {Number(item.quantity || 0)}</span>
            {item.product_code ? <span>· {item.product_code}</span> : null}
            {method?.name ? <span>· {method.name}</span> : null}
          </div>
        </div>

        {/* Status pill + editable dropdown */}
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`inline-flex items-center px-2 py-1 text-[0.65rem] font-bold tracking-widest uppercase border ${band}`}
          >
            {t(`production_status_${item.production_status}`)}
          </span>
          <select
            data-testid={DASHBOARD.productionStatusSelect(item.item_id)}
            value={item.production_status}
            onChange={(e) => onChangeStatus(e.target.value)}
            className="ml-auto text-xs border border-[var(--border)] px-2 py-1 font-mono-tech focus:outline-none focus:border-black"
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`production_status_${s}`)}
              </option>
            ))}
          </select>
        </div>

        {/* Owner + assignee select */}
        <div className="flex items-center gap-2">
          <div
            className="flex items-center justify-center w-8 h-8 rounded-full border-2 border-black bg-[var(--surface-alt)] font-mono-tech font-bold text-[0.65rem] flex-shrink-0"
            title={employee?.name || t("production_assignee_unassigned")}
          >
            {employee ? initialsOf(employee.name) : "—"}
          </div>
          <select
            data-testid={DASHBOARD.productionAssigneeSelect(item.item_id)}
            value={item.assigned_employee_id || ""}
            onChange={(e) => onAssign(e.target.value)}
            className="flex-1 text-xs border border-[var(--border)] px-2 py-1 font-mono-tech focus:outline-none focus:border-black"
          >
            <option value="">{t("production_assignee_unassigned")}</option>
            {employees.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
        </div>

        {/* Blocker chips */}
        <div className="flex flex-wrap gap-1.5">
          {blockers.map(([field, labelKey, ok, testId]) => (
            <button
              key={field}
              type="button"
              data-testid={testId}
              onClick={() => onToggleBlocker(field, !ok)}
              className={`px-2 py-1 text-[0.65rem] font-mono-tech tracking-wider uppercase border transition-colors ${
                ok
                  ? "bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                  : "bg-red-50 border-red-400 text-red-700 hover:bg-red-100 animate-[pulse_2s_ease-in-out_infinite]"
              }`}
              title={ok ? t(`production_blocker_${BLOCKER_OK_KEY[field]}`) : t(labelKey)}
            >
              {ok ? "✓ " : "✗ "}
              {ok ? t(`production_blocker_${BLOCKER_OK_KEY[field]}`) : t(labelKey)}
            </button>
          ))}
        </div>

        {missingCount > 0 && (
          <div className="font-mono-tech text-[0.65rem] tracking-wider text-red-700">
            ⚠ {missingCount} {missingCount === 1 ? "BLOQUEADOR" : "BLOQUEADORES"}
          </div>
        )}
      </div>

      {/* Footer — delivery date */}
      <footer className="px-3 py-2 border-t border-[var(--border)] font-mono-tech text-[0.65rem] tracking-wider text-[var(--text-secondary)] flex justify-between">
        <span>ENTREGA</span>
        <span>{formatDate(item.delivery_date)}</span>
      </footer>
    </article>
  );
}
